import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FULL_DEAL_MAPPING, FULL_OUTPUT_MAPPING } from "@/lib/excel/mapping";
import { templateService } from "@/lib/services/templateService";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  onSuccess?: () => void;
}

export const TemplateUploaderToSupabase = ({ onSuccess }: Props) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadStep, setUploadStep] = useState<string>("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !name) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    setLoading(true);

    try {
      setUploadStep("Converting to base64...");

      setUploadStep("Uploading to Supabase Storage...");

      setUploadStep("Saving to database...");

      await templateService.saveTemplate({
        name,
        description,
        file,
        mapping: FULL_DEAL_MAPPING,
        outputs: FULL_OUTPUT_MAPPING,
      });

      setUploadStep("✅ Complete!");

      toast.success("Template uploaded successfully with storage backup!");

      setName("");
      setDescription("");
      setFile(null);

      const fileInput = document.getElementById(
        "template-file",
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      onSuccess?.();
    } catch (error) {
      console.error("❌ Error saving template:", error);
      toast.error(
        `Error uploading template: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setUploadStep("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Template Name *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter template name"
          required
          disabled={loading}
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter template description"
          rows={3}
          disabled={loading}
        />
      </div>

      <div>
        <Label htmlFor="template-file">Excel File *</Label>
        <Input
          id="template-file"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          required
          disabled={loading}
        />
        {file && (
          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-600">
              Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </p>
          </div>
        )}
      </div>

      {loading && (
        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 text-blue-700">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">{uploadStep}</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full w-2/3 animate-pulse"></div>
          </div>
          <p className="text-xs text-blue-600">
            File will be stored both in Storage and as base64 backup
          </p>
        </div>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="bg-primary-blue w-full"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Uploading...
          </>
        ) : (
          "Upload to Supabase (Storage + Base64)"
        )}
      </Button>

      <div className="text-xs text-gray-500 space-y-1 mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="font-medium text-gray-700">📦 Dual backup system:</p>
        <p>• 📁 Supabase Storage - primary storage for fast access</p>
        <p>• 💾 Base64 in database - backup if storage fails</p>
        <p>• 🔗 Public URL - easy sharing and downloading</p>
      </div>
    </form>
  );
};
